<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue'
import { Check, ChevronDown, Languages } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { appLocales, type AppLocale } from '@/i18n/locales'
import { useAppStore } from '@/stores/app'

const { t } = useI18n()
const app = useAppStore()
const id = useId()
const trigger = ref<HTMLButtonElement>()
const menu = ref<HTMLElement>()
const open = ref(false)
const focused = ref(0)
const position = ref({ left: '0px', top: '0px', width: '224px' })
const placement = ref<'above' | 'below'>('below')
const current = computed(() => appLocales.find(item => item.value === app.locale)!)
const shortNames = { 'zh-CN': '简', 'zh-TW': '繁', en: 'EN', ja: '日', ko: '한' }

function positionMenu(): void {
  const rect = trigger.value?.getBoundingClientRect()
  if (!rect) return
  const width = Math.min(Math.max(224, rect.width), window.innerWidth - 24)
  const height = menu.value?.offsetHeight || 272
  placement.value = window.innerHeight - rect.bottom < height + 20 && rect.top > height + 20 ? 'above' : 'below'
  position.value = {
    left: `${Math.min(Math.max(12, rect.left), window.innerWidth - width - 12)}px`,
    top: `${placement.value === 'above' ? Math.max(12, rect.top - height - 8) : rect.bottom + 8}px`,
    width: `${width}px`,
  }
}
async function show(last = false): Promise<void> {
  focused.value = last ? appLocales.length - 1 : appLocales.findIndex(item => item.value === app.locale)
  positionMenu()
  open.value = true
  await nextTick()
  if (!open.value) return
  positionMenu()
  focusOption()
}
function close(restoreFocus = false): void {
  open.value = false
  if (restoreFocus) trigger.value?.focus({ preventScroll: true })
}
function select(locale: AppLocale): void {
  app.locale = locale
  close(true)
}
function focusOption(): void {
  menu.value?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]')[focused.value]?.focus({ preventScroll: true })
}
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return }
  if (event.key === 'Tab') { close(true); return }
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  focused.value = event.key === 'Home' ? 0 : event.key === 'End' ? appLocales.length - 1
    : (focused.value + (event.key === 'ArrowDown' ? 1 : -1) + appLocales.length) % appLocales.length
  focusOption()
}
function onPointer(event: PointerEvent): void {
  if (event.target instanceof Node && !trigger.value?.contains(event.target) && !menu.value?.contains(event.target)) close()
}
function onFocus(event: FocusEvent): void {
  if (open.value && event.target instanceof Node && !trigger.value?.contains(event.target) && !menu.value?.contains(event.target)) close()
}
function onScroll(event: Event): void {
  if (open.value && !(event.target instanceof Node && menu.value?.contains(event.target))) close()
}
function onResize(): void { if (open.value) positionMenu() }
onMounted(() => {
  document.addEventListener('pointerdown', onPointer, true)
  document.addEventListener('focusin', onFocus)
  document.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onPointer, true)
  document.removeEventListener('focusin', onFocus)
  document.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <div class="language-select">
    <button :id="`${id}-trigger`" ref="trigger" class="language-trigger" type="button" :class="{ 'is-open': open }"
      :aria-label="`${t('app.language')} · ${current.label}`" aria-haspopup="menu" :aria-expanded="open" :aria-controls="open ? id : undefined"
      @click="open ? close() : show()" @keydown.down.prevent="show()" @keydown.up.prevent="show(true)" @keydown.esc.prevent="close(true)">
      <span class="language-symbol"><Languages aria-hidden="true" /></span>
      <span class="language-current" :lang="app.locale">{{ current.label }}</span>
      <span class="language-compact" aria-hidden="true">{{ shortNames[app.locale] }}</span>
      <ChevronDown class="language-chevron" aria-hidden="true" />
    </button>
    <Teleport to="body">
      <Transition name="language-pop">
        <div v-if="open" :id="id" ref="menu" class="language-menu" :class="`language-menu--${placement}`" :style="position"
          role="menu" :aria-labelledby="`${id}-trigger`" @keydown="onKeydown">
          <div class="language-menu-heading" aria-hidden="true">{{ t('app.language') }}<Languages /></div>
          <button v-for="(item, index) in appLocales" :key="item.value" type="button" class="language-option"
            role="menuitemradio" :aria-checked="app.locale === item.value" :tabindex="focused === index ? 0 : -1"
            :class="{ 'is-selected': app.locale === item.value }" :lang="item.value" @focus="focused = index" @click="select(item.value)">
            <span class="language-option-symbol" aria-hidden="true">{{ shortNames[item.value] }}</span>
            <span class="language-option-name">{{ item.label }}</span>
            <Check class="language-check" aria-hidden="true" />
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* Motion intentionally remains enabled regardless of OS reduced-motion settings. */
.language-select { width: 12rem; max-width: 100%; flex-shrink: 0; }
.language-trigger { width: 100%; min-height: 2.75rem; display: flex; align-items: center; gap: .625rem; padding: .375rem .6875rem; border: 1px solid var(--border); border-radius: .5rem; background: var(--surface); color: var(--muted-strong); font: inherit; font-size: var(--font-caption, .8125rem); font-weight: 550; cursor: pointer; transition: background-color 180ms ease, border-color 180ms ease, box-shadow 220ms ease, color 180ms ease !important; }
.language-trigger:hover { color: var(--text-strong); background: var(--surface-hover); border-color: var(--border-strong); }
.language-trigger.is-open { color: var(--primary); border-color: color-mix(in srgb, var(--primary) 35%, var(--border)); background: var(--primary-soft); box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 7%, transparent); }
.language-symbol { display: grid; place-items: center; flex: none; }
.language-symbol svg { width: 1.0625rem; height: 1.0625rem; }
.language-current { flex: 1; text-align: left; white-space: nowrap; }
.language-chevron { flex: none; width: .875rem; height: .875rem; margin-left: auto; color: var(--muted); transition: transform 260ms cubic-bezier(.2,.8,.2,1), color 180ms ease !important; }
.is-open .language-chevron { transform: rotate(180deg); color: var(--primary); }
.language-trigger:focus-visible, .language-option:focus-visible { outline: 2px solid var(--primary); outline-offset: 3px; }
.language-menu { position: fixed; z-index: 3100; padding: .375rem; max-height: calc(100dvh - 1.5rem); overflow-y: auto; border: 1px solid var(--border); border-radius: .875rem; background: var(--surface-raised, var(--surface)); box-shadow: 0 12px 36px rgb(15 23 42 / 13%), 0 2px 8px rgb(15 23 42 / 5%); transform-origin: top center; }
.language-menu--above { transform-origin: bottom center; }
.language-menu-heading { display: flex; align-items: center; justify-content: space-between; min-height: 2.25rem; padding: .25rem .625rem .5rem; margin-bottom: .25rem; border-bottom: 1px solid var(--border); color: var(--muted); font-size: .6875rem; font-weight: 550; }
.language-menu-heading svg { width: .875rem; height: .875rem; opacity: .7; }
.language-option { display: flex; align-items: center; gap: .75rem; width: 100%; min-height: 2.75rem; padding: .375rem .625rem; border: 0; border-radius: .5rem; background: transparent; color: var(--muted-strong); font: inherit; font-size: .8125rem; text-align: left; cursor: pointer; transition: background-color 160ms ease, color 160ms ease, transform 180ms cubic-bezier(.2,.8,.2,1) !important; }
.language-option:hover, .language-option:focus-visible { background: var(--surface-hover); color: var(--text-strong); }
.language-option:active { transform: scale(.98); }
.language-option.is-selected { color: var(--primary); background: var(--primary-soft); }
.language-option-symbol { width: 1.625rem; height: 1.625rem; display: grid; place-items: center; flex: none; border-radius: .375rem; background: var(--surface-subtle); color: var(--muted); font-size: .6875rem; font-weight: 650; transition: background-color 180ms ease, color 180ms ease !important; }
.is-selected .language-option-symbol { color: var(--primary); background: color-mix(in srgb, var(--primary) 10%, var(--surface)); }
.language-option-name { flex: 1; }
.language-check { width: 1rem; height: 1rem; flex: none; opacity: 0; transform: scale(.7); transition: opacity 180ms ease, transform 220ms cubic-bezier(.2,.8,.2,1) !important; }
.is-selected .language-check { opacity: 1; transform: scale(1); }
.language-pop-enter-active { transition: opacity 180ms ease, transform 260ms cubic-bezier(.16,1,.3,1) !important; }
.language-pop-leave-active { transition: opacity 140ms ease, transform 180ms cubic-bezier(.4,0,1,1) !important; pointer-events: none; }
.language-pop-enter-from, .language-pop-leave-to { opacity: 0; transform: translateY(-6px) scale(.97); }
.language-menu--above.language-pop-enter-from, .language-menu--above.language-pop-leave-to { transform: translateY(6px) scale(.97); }
.sidebar-language { width: 100%; }
.language-compact { display: none; }
.topbar-language { width: auto; }
.topbar-language .language-trigger { gap: .375rem; padding-inline: .5rem; }
.topbar-language .language-current { display: none; }
.topbar-language .language-compact { display: block; }
.topbar-language .language-chevron { width: .75rem; margin-left: 0; }
@media (min-width: 47.501rem) and (max-width: 70rem) {
  .sidebar-language .language-trigger { padding: .5rem .25rem; justify-content: center; gap: .125rem; }
  .sidebar-language .language-symbol, .sidebar-language .language-current { display: none; }
  .sidebar-language .language-compact { display: block; font-size: .75rem; }
  .sidebar-language .language-chevron { margin-left: 0; width: .625rem; }
}
</style>
